import { gql, useQuery } from "@apollo/client";
import { Card, Grid, MenuItem, Select } from "@mui/material";
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import useMe from "hooks/authentication/useMe";
import useGetAllOrganizationUsers from "hooks/users/useGetAllOrganizationUsers";
import { useEffect, useMemo, useState } from "react";
import formatCentsToUSDNoDecimals from "utils/money/formatCentsToUSDNoDecimals";

const GET_PROPOSAL_USER_METRICS = gql`
  query getProposalUserMetrics($userId: ID) {
    getProposalUserMetrics(userId: $userId) {
      userId
      sentCount
      wonCount
      totalProposed
      totalWon
      salesGoal
      closeRate
      avgNetMargin
    }
  }
`;

type ProposalUserMetrics = {
  userId: string;
  sentCount: number;
  wonCount: number;
  totalProposed: string;
  totalWon: string;
  salesGoal: string;
  closeRate: number;
  avgNetMargin: number;
};

function roundPercent(v: number | null | undefined): string {
  const n = Number(v || 0);
  if (!isFinite(n)) return "0%";
  return `${Math.round(n)}%`;
}

function safeToNumberFromBigIntString(v: string | null | undefined): number {
  try {
    return Number(BigInt(v || "0"));
  } catch {
    return 0;
  }
}

function CenterMetricCard({
  title,
  primary,
  secondary,
}: {
  title: string;
  primary: string;
  secondary?: string;
}) {
  return (
    <Card>
      <MDBox p={2} textAlign="center">
        <MDTypography variant="button" fontWeight="medium" color="text" textTransform="capitalize">
          {title}
        </MDTypography>

        <MDBox mt={0.75} lineHeight={1}>
          <MDTypography variant="h4" fontWeight="bold">
            {primary}
          </MDTypography>

          {!!secondary && (
            <MDBox mt={0.75}>
              <MDTypography variant="button" color="text">
                {secondary}
              </MDTypography>
            </MDBox>
          )}
        </MDBox>
      </MDBox>
    </Card>
  );
}

export default function ProposalMetricCards() {
  const [fetchMe, { user }] = useMe();
  const { data: orgUsersData } = useGetAllOrganizationUsers();

  const [selectedUserId, setSelectedUserId] = useState<string>("");

  const canViewAllProposals =
    (user as any)?.permissions?.includes("VIEW_ALL_PROPOSALS") ?? false;

  useEffect(() => {
    fetchMe();
  }, []);

  useEffect(() => {
    if (!selectedUserId && user?.id) {
      setSelectedUserId(String(user.id));
    }
  }, [user?.id, selectedUserId]);

  useEffect(() => {
    if (!user?.id) return;
    if (!canViewAllProposals && selectedUserId !== String(user.id)) {
      setSelectedUserId(String(user.id));
    }
  }, [canViewAllProposals, selectedUserId, user?.id]);

  const effectiveUserId = canViewAllProposals ? selectedUserId : String(user?.id ?? "");

  const { data, loading } = useQuery(GET_PROPOSAL_USER_METRICS, {
    variables: { userId: effectiveUserId || undefined },
    skip: !effectiveUserId,
    fetchPolicy: "network-only",
  });

  const metrics: ProposalUserMetrics | null = data?.getProposalUserMetrics ?? null;

  const dropdownUsers = useMemo(() => {
    const users = (orgUsersData ?? []).map((u: any) => ({
      id: String(u.id),
      label: u.name || `${u.firstName ?? ""} ${u.lastName ?? ""}`.trim() || `User ${u.id}`,
    }));
    users.sort((a: any, b: any) => a.label.localeCompare(b.label));
    return users;
  }, [orgUsersData]);

  const sent = metrics?.sentCount ?? 0;
  const won = metrics?.wonCount ?? 0;
  const closeRate = metrics?.closeRate ?? 0;
  const margin = metrics?.avgNetMargin ?? 0;

  const wonCents = metrics?.totalWon ?? "0";
  const proposedCents = metrics?.totalProposed ?? "0";
  const goalCents = metrics?.salesGoal ?? "0";

  const goalIsSet = (() => {
    try {
      return BigInt(goalCents || "0") > 0n;
    } catch {
      return false;
    }
  })();

  const percentToGoal = useMemo(() => {
    if (!goalIsSet) return null;
    const wonNum = safeToNumberFromBigIntString(wonCents);
    const goalNum = safeToNumberFromBigIntString(goalCents);
    if (!goalNum) return null;
    return (wonNum / goalNum) * 100;
  }, [wonCents, goalCents, goalIsSet]);

  const paceToGoal = useMemo(() => {
    if (!goalIsSet) return null;

    const wonNum = safeToNumberFromBigIntString(wonCents);
    const goalNum = safeToNumberFromBigIntString(goalCents);
    if (!goalNum) return null;

    const now = new Date();
    const start = new Date(now.getFullYear(), 0, 1);
    const dayOfYear = Math.max(
      1,
      Math.floor((now.getTime() - start.getTime()) / (1000 * 60 * 60 * 24)) + 1
    );

    const daysInYear = 365;
    const projected = (wonNum / dayOfYear) * daysInYear;
    return (projected / goalNum) * 100;
  }, [wonCents, goalCents, goalIsSet]);

  return (
    <Card>
      <MDBox p={2}>
        {/* Heading row */}
        <MDBox
          display="flex"
          justifyContent="space-between"
          alignItems={{ xs: "flex-start", sm: "center" }}
          flexDirection={{ xs: "column", sm: "row" }}
          gap={1.5}
          mb={2}
        >
          <MDBox>
            <MDTypography variant="h4" fontWeight="medium">
              Proposals
            </MDTypography>
            <MDTypography variant="button" color="text">
              {loading ? "Loading…" : "Year to date"}
            </MDTypography>
          </MDBox>

          {canViewAllProposals ? (
            <MDBox minWidth={{ xs: "100%", sm: 280 }}>
              <Select
                fullWidth
                size="small"
                value={selectedUserId}
                onChange={(e) => setSelectedUserId(String(e.target.value))}
              >
                {dropdownUsers.map((u: any) => (
                  <MenuItem key={u.id} value={u.id}>
                    {u.label}
                  </MenuItem>
                ))}
              </Select>
            </MDBox>
          ) : (
            <MDBox>
              <MDTypography variant="button" color="text">
                Salesperson: You
              </MDTypography>
            </MDBox>
          )}
        </MDBox>

        <Grid container spacing={2}>
          {/* Row 1 */}
          <Grid item xs={12} sm={6} md={4}>
            <CenterMetricCard title="Sent / Won" primary={`${sent} / ${won}`} />
          </Grid>

          <Grid item xs={12} sm={6} md={4}>
            <CenterMetricCard
              title="Total Proposed"
              primary={formatCentsToUSDNoDecimals(proposedCents)}
            />
          </Grid>

          <Grid item xs={12} sm={6} md={4}>
            <CenterMetricCard
              title="Won / Goal"
              primary={`${formatCentsToUSDNoDecimals(wonCents)} / ${
                goalIsSet ? formatCentsToUSDNoDecimals(goalCents) : "—"
              }`}
            />
          </Grid>

          {/* Row 2: center the 2 cards on md+ by adding spacers */}
          <Grid item xs={false as any} sm={false as any} md={2} />

          <Grid item xs={12} sm={6} md={4}>
            <CenterMetricCard
              title="Close Rate/ Proposed Net Margin"
              primary={`${roundPercent(closeRate)} / ${roundPercent(margin)}`}
            />
          </Grid>

          <Grid item xs={12} sm={6} md={4}>
            <CenterMetricCard
              title="To Goal / Pace"
              primary={
                goalIsSet
                  ? `${roundPercent(percentToGoal ?? 0)} / ${roundPercent(paceToGoal ?? 0)}`
                  : "— / —"
              }
            />
          </Grid>

          <Grid item xs={false as any} sm={false as any} md={2} />
        </Grid>
      </MDBox>
    </Card>
  );
}
